import React, { useMemo } from 'react';
import { Modal, Transfer, SearchBox, Button, ConfigProvider } from 'tea-component';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import BasicImage from '@home/components/BasicImage';
import MobileGameIcon from '@/pages/benchmark/assets/svg/common/default-game.svg';
import ConsoleGameIcon from '@/pages/home/assets/img/common/consolegame-default.png';
import { LANGUAGE_EN } from '@home/_config/config';
import RightTable from './RightTable';
import LeftTable from './LeftTable';
import { ICompareGameFilter } from './_type';
import useFilterState from './_hooks/useFilterState';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import TextBubbles from '@home/components/TextBubbles';
import TipContext from '@home/components/TipContext';

export default function CompareGameFilter(props: ICompareGameFilter) {
  const {
    visible,
    onClose,
    onConfirm,
    value,
    selectedLoading = false,
    maxSelected = 9,
    api,
    getData,
    frameType,
    extraParams,
    tip,
  } = props;
  const { formatMessage } = useIntl();
  const isEN = getLocale() === LANGUAGE_EN;
  const currentGame = useSelector((state: any) => state?.dashboard?.currentGame, shallowEqual);
  const isPc = frameType ? frameType === 'pc' : (currentGame?.data_frame === 'pc');
  const {
    selectedList,
    handelOnSearch,
    renderGameList,
    error,
    allLoading,
    setSelectedList,
  } = useFilterState({
    value,
    api,
    frameType,
    extraParams,
    getData,
    visible
  });

  // 表格列配置
  const columns = useMemo(() => [
    {
      key: '',
      header: '',
      render: (cvm: any) => {
        const { cover, version, game_name, isTitleRow, title } = cvm;
        return (
          isTitleRow ? <div className='title'>{formatMessage({id: title})}</div> :
            <div className="add-games-flex">
              <div className="games-img">
                <BasicImage src={cover} errorSrc={isPc ? ConsoleGameIcon : MobileGameIcon} />
              </div>
              <div className="games-context">
                <TextBubbles
                  className="games-title"
                  text={
                    game_name
                  }
                />
                <div className="games-desc">
                  <div className="games-text">
                    <span className="dbd-game-transfer-row-date">
                      {`${version}`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
        );
      },
    },
  ], [isEN, isPc]);

  return (
    <ConfigProvider locale={isEN ? "en" : "zh"}>
      <Modal
        className="dbd-add-compare"
        size={800}
        visible={visible}
        caption={formatMessage({ id: 'benchmark_add_games_to_compare' })}
        onClose={() =>  {
          setSelectedList(value);
          onClose();
        }}
      >
        <Modal.Body>
          <Transfer
            leftCell={
              <Transfer.Cell
                scrollable={false}
                title={formatMessage({ id: 'benchamrk_add' })}
                header={
                  <SearchBox
                    disabled={allLoading}
                    placeholder={formatMessage({ id: 'benchamrk_add_search_any_game' })}
                    onChange={(value) => handelOnSearch(value)}
                    onSearch={(value) => handelOnSearch(value)}
                  />
                }
              >
                <Abnormal
                  isLoading={false}
                  isError={error}
                  component={
                    <LeftTable
                      loading={allLoading}
                      dataSource={renderGameList}
                      maxSelected={maxSelected}
                      columns={columns}
                      selectedIdList={selectedList?.map(
                        (item: any) => item.game_id,
                      )}
                      onChange={(keys, { selectedRecords }) => {
                        const otherKey = [...(keys || {})];
                        const newSelectedList = selectedList.filter((item: any) => {
                          const index = otherKey.indexOf(item.game_id);
                          if (index > -1) {
                            otherKey.splice(index, 1);
                            return true;
                          }
                          return false;
                        });
                        const otherRecords =
                          otherKey.length === 0
                            ? []
                            : selectedRecords.filter((item: any) =>
                              otherKey.includes(item.game_id),
                            );
                        setSelectedList([
                          ...(newSelectedList || []),
                          ...(otherRecords?.map((item: any) => ({
                            ...(item || {}),
                            selected_edition_unified_id: item.game_id,
                          })) || []),
                        ]);
                      }}
                    />
                  }
                />

              </Transfer.Cell>
            }
            rightCell={
              <Transfer.Cell
                scrollable={false}
                style={{ height: '100%' }}
                title={
                  <div className="text-row-show">
                    <div className="text-left">
                      {`${formatMessage({ id: 'benchmark_selected' })} (${selectedList.length}/${maxSelected})`}
                      {tip && <TipContext tipText={tip} className="dbd-assessment-filter-tip" />}
                    </div>
                    {selectedList.length > 0 ? (
                      <Button
                        type="link"
                        className="text-clear"
                        onClick={() => setSelectedList([])}
                      >
                        {formatMessage({ id: 'benchmark_clear_all' })}
                      </Button>
                    ) : null}
                  </div>
                }
              >
                <RightTable
                  loading={selectedLoading}
                  dataSource={selectedList}
                  columns={columns}
                  onRemove={(key: any) => {
                    setSelectedList(
                      selectedList?.filter((i: any) => i.game_id !== key),
                    );
                  }}
                  // gameCountryMap={gameCountryMap}
                  onDragEnd={(records: Array<any>) => {
                    setSelectedList(records);
                  }}
                />
              </Transfer.Cell>
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            disabled={selectedList?.length === 0}
            onClick={() => {
              onConfirm?.(selectedList);
              onClose?.();
            }}
          >
            {formatMessage({ id: 'common_confirm' })}
          </Button>
          <Button type="weak" onClick={() => {
            setSelectedList(value);
            onClose();
          }}>
            {formatMessage({ id: 'common_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfigProvider>
  );
}
